/**
 * ChatAssistant — AI Visitor Assistant floating chat widget.
 *
 * WHAT THIS COMPONENT DOES:
 * 1. Renders a floating button (bottom-right)
 * 2. On click, opens a chat panel
 * 3. Visitor types a question (or clicks a starter prompt)
 * 4. Component POSTs to /api/chat/stream and reads the SSE response
 * 5. Tokens arrive one by one → appended to the assistant bubble in real time
 *
 * HOW STREAMING WORKS IN REACT:
 * fetch() returns response.body — a ReadableStream.
 * We attach a reader and loop: reader.read() → { value, done }
 * Each value is a Uint8Array → decode to string → parse SSE lines.
 * For each "token" event: setMessages(prev => append token to last message)
 * This pattern causes a re-render per token batch, which React batches
 * efficiently. The result is smooth character-by-character rendering.
 *
 * CHANGE THIS FOR PRODUCTION:
 * Replace API_BASE_URL with your deployed backend URL.
 */

import { useState, useRef, useEffect } from "react";
import {
  FloatingButton,
  ChatPanel,
  Header,
  HeaderAvatar,
  HeaderText,
  CloseButton,
  MessagesArea,
  WelcomeContainer,
  WelcomeText,
  StarterPromptsGrid,
  StarterPrompt,
  MessageRow,
  Bubble,
  StreamCursor,
  TypingIndicator,
  InputArea,
  TextInput,
  SendButton,
} from "./ChatStyle";

// In development: uses localhost:5001
// In production: set REACT_APP_API_URL in your frontend hosting environment
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

// ── Starter prompts shown before any conversation begins ─────────────────────
const STARTER_PROMPTS = [
  "What projects has Harsh built?",
  "What are Harsh's DevOps and cloud skills?",
  "Tell me about Harsh's work experience",
  "Where did Harsh study?",
  "How can I contact Harsh?",
];

// ─── SVG Icons (inline — no extra dependency needed) ─────────────────────────

const ChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
    strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
    strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const ChatAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");

  // isStreaming: true while tokens are still arriving from the server.
  // Used to show the blinking cursor and disable the send button.
  const [isStreaming, setIsStreaming] = useState(false);

  // isWaiting: true between sending question and receiving the first token.
  // Used to show the "..." typing indicator before the response starts.
  const [isWaiting, setIsWaiting] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to latest message on every update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isWaiting]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen]);

  // Fix mobile viewport height — 100vh is unreliable on iOS/Android because
  // the browser's address bar changes the visible height. This sets --vh to
  // the real visible pixel height, which ChatStyle.js uses for the panel height.
  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    setVh();
    window.addEventListener("resize", setVh);
    return () => window.removeEventListener("resize", setVh);
  }, []);

  /**
   * sendMessage
   *
   * The core function. Sends the question to the streaming endpoint and
   * processes the SSE response token by token.
   *
   * FLOW:
   * 1. Add user message to state
   * 2. Show typing indicator (isWaiting = true)
   * 3. POST to /api/chat/stream
   * 4. Get ReadableStream from response.body
   * 5. Read chunks → decode → parse SSE lines
   * 6. "token" events: append to assistant bubble
   * 7. "done" event: stop streaming, remove cursor
   * 8. "error" event: show error message
   */
  const sendMessage = async (questionText) => {
    const question = (questionText || input).trim();
    if (!question || isStreaming) return;

    setInput("");

    // Add the user's message to the conversation
    const userMessage = { id: Date.now(), role: "user", text: question };
    setMessages((prev) => [...prev, userMessage]);

    // Show typing indicator while waiting for first token
    setIsWaiting(true);
    setIsStreaming(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      // response.body is a ReadableStream — this is the streaming interface
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // The ID for the assistant message we're about to build token by token
      const assistantMsgId = Date.now() + 1;

      // Buffer for incomplete SSE lines that span multiple read() chunks
      let buffer = "";
      let firstTokenReceived = false;

      // Read loop — runs until the server sends "done" or stream ends
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        // Decode the raw bytes to a string and add to our line buffer
        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by \n\n — split on double newlines
        const parts = buffer.split("\n\n");

        // The last part might be incomplete — keep it in the buffer
        buffer = parts.pop() || "";

        for (const part of parts) {
          // Each SSE event looks like: "data: {...json...}"
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6); // remove "data: " prefix
          let event;
          try {
            event = JSON.parse(jsonStr);
          } catch {
            continue; // skip malformed events
          }

          if (event.type === "token") {
            // First token arriving — hide typing indicator, add the bubble
            if (!firstTokenReceived) {
              firstTokenReceived = true;
              setIsWaiting(false);
              // Add empty assistant bubble — we'll fill it token by token
              setMessages((prev) => [
                ...prev,
                { id: assistantMsgId, role: "assistant", text: "", isStreaming: true },
              ]);
            }

            // Append this token to the assistant bubble.
            // Functional update ensures we always operate on the latest state —
            // critical during fast streams to avoid stale closure issues.
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMsgId
                  ? { ...msg, text: msg.text + event.token }
                  : msg
              )
            );
          } else if (event.type === "done") {
            // Stream finished — remove the streaming cursor
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMsgId
                  ? { ...msg, isStreaming: false }
                  : msg
              )
            );
            setIsStreaming(false);
            setIsWaiting(false);
          } else if (event.type === "error") {
            // Server sent an error through the stream
            setIsWaiting(false);
            setMessages((prev) => [
              ...prev,
              {
                id: assistantMsgId,
                role: "assistant",
                text: event.message || "Something went wrong. Please try again.",
                isStreaming: false,
                isError: true,
              },
            ]);
            setIsStreaming(false);
          }
        }
      }
    } catch (err) {
      // Network error or server unreachable
      setIsWaiting(false);
      setIsStreaming(false);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 2,
          role: "assistant",
          text: "The assistant is temporarily unavailable. Please try again in a moment.",
          isStreaming: false,
          isError: true,
        },
      ]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* ── Floating toggle button ─────────────────────────────────────────── */}
      <FloatingButton
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? "Close chat" : "Open AI assistant"}
        title="Ask about Harsh"
      >
        <ChatIcon />
      </FloatingButton>

      {/* ── Chat panel (rendered only when open) ──────────────────────────── */}
      {isOpen && (
        <ChatPanel>
          {/* Header */}
          <Header>
            <HeaderAvatar>
              <SparkleIcon />
            </HeaderAvatar>
            <HeaderText>
              <h4>Ask about Harsh</h4>
              <p>AI-powered • Answers from portfolio data</p>
            </HeaderText>
            <CloseButton onClick={handleClose} aria-label="Close">
              <CloseIcon />
            </CloseButton>
          </Header>

          {/* Messages thread */}
          <MessagesArea>
            {/* Starter prompts — shown only before any messages */}
            {messages.length === 0 && (
              <WelcomeContainer>
                <WelcomeText>
                  Hi! Ask me anything about Harsh's projects, skills, or experience.
                </WelcomeText>
                <StarterPromptsGrid>
                  {STARTER_PROMPTS.map((prompt) => (
                    <StarterPrompt
                      key={prompt}
                      onClick={() => sendMessage(prompt)}
                      disabled={isStreaming}
                    >
                      {prompt}
                    </StarterPrompt>
                  ))}
                </StarterPromptsGrid>
              </WelcomeContainer>
            )}

            {/* Message bubbles */}
            {messages.map((msg) => (
              <MessageRow key={msg.id} $isUser={msg.role === "user"}>
                <Bubble $isUser={msg.role === "user"}>
                  {msg.text}
                  {/* Blinking cursor during active streaming */}
                  {msg.isStreaming && <StreamCursor />}
                </Bubble>
              </MessageRow>
            ))}

            {/* Typing indicator — shown while waiting for first token */}
            {isWaiting && (
              <TypingIndicator>
                <span /><span /><span />
              </TypingIndicator>
            )}

            {/* Invisible anchor for auto-scroll */}
            <div ref={messagesEndRef} />
          </MessagesArea>

          {/* Input area */}
          <InputArea>
            <TextInput
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question..."
              disabled={isStreaming}
              maxLength={500}
            />
            <SendButton
              onClick={() => sendMessage()}
              disabled={!input.trim() || isStreaming}
              aria-label="Send"
            >
              <SendIcon />
            </SendButton>
          </InputArea>
        </ChatPanel>
      )}
    </>
  );
};

export default ChatAssistant;
